import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdCreate } from "react-icons/md";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";

const PricingSettingsListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [pricingSettingsRecordList, setPricingSettingsRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalPricingSettings, setTotalPricingSettings] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);


  /**************************************************
  //Event definitions
  **************************************************/

  //onclick of edit pricing settings
  const handleEditPricingSettingsClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;

    navigate(
      {
        pathname: "/pricingSettings",
        search: createSearchParams({
          pricingsettingsid: param.recordResponse.SettingsId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedPricingSettingsChanges = (event) => {
    let _navigationData = { previousPath: "/pricingsettingslist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setPricingSettingsList = (pricingSettingsList) => {
    try {
      pricingSettingsList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _pricingSettingsList = pricingSettingsList.map((item) => {
        return {
          id: item.SettingsId,
          Description: item.Description,
          Unit: item.Unit,
          SettingsKey: item.SettingsKey,
          SettingsValue: item.SettingsValue,
          recordResponse: item,
        };
      });

      setPricingSettingsRecordList(_pricingSettingsList);
      setTotalPricingSettings(_pricingSettingsList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchPricingSettingsList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getPricingSettingsList",
        "pricingsettings"
      );
      setPricingSettingsList(_lists);
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "SettingsKey",
      headerName: t("pricingSettingsList.settingsKey"),
      flex: 1,
    },
    {
      field: "SettingsValue",
      headerName: t("pricingSettingsList.settingsValue"),
      flex: 1,
    },
    {
      field: "Unit",
      headerName: t("pricingSettingsList.unit"),
      flex: 1,
    },
    {
      field: "Description",
      headerName: t("pricingSettingsList.description"),
      flex: 1,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(pricingSettingsRecordList, "pricingsettingslist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedPricingSettingsChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditPricingSettingsClick,
      buttonColor: "#EEEEE",
      action: "edit"
    }
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchPricingSettingsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={pricingSettingsRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalPricingSettings}
        toolbarHeader={t("pricingSettingsList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
    </FormPage>
  );
};

export default PricingSettingsListForm;
